import React, { useState, useRef, useEffect } from 'react';
import { 
  FaEnvelope, 
  FaPhoneAlt, 
  FaMapMarkerAlt, 
  FaCheckCircle, 
  FaArrowRight,
  FaRegClock,
  FaRegLightbulb,
  FaUserAlt,
  FaClock,
  FaChevronDown
} from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const ContactForm = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    budget: '',
    timeline: ''
  });
  
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const { currentTheme } = useTheme();
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);
  const { currentLanguage } = useLanguage();
  
  // Translations
  const translations = {
    ru: {
      title: "Свяжитесь с нами",
      subtitle: "Готовы обсудить ваш проект? Заполните форму ниже, и мы свяжемся с вами в ближайшее время.",
      formTitle: "Расскажите о вашем проекте",
      nameLabel: "Имя *",
      namePlaceholder: "Ваше имя",
      nameError: "Пожалуйста, введите ваше имя",
      emailLabel: "Email *",
      emailPlaceholder: "Ваш email",
      emailError: "Пожалуйста, введите email",
      emailInvalidError: "Некорректный email",
      phoneLabel: "Телефон (опционально)",
      phonePlaceholder: "Ваш телефон",
      budgetLabel: "Бюджет (опционально)",
      budgetPlaceholder: "Выберите бюджет",
      timelineLabel: "Сроки (опционально)",
      messageLabel: "Сообщение *",
      messagePlaceholder: "Расскажите подробнее о вашем проекте...",
      messageError: "Пожалуйста, введите сообщение",
      submitButton: "Отправить сообщение",
      sending: "Отправка...",
      privacy: "Нажимая кнопку 'Отправить', вы соглашаетесь с нашей",
      privacyLink: "политикой конфиденциальности",
      thankYouTitle: "Спасибо за ваше сообщение!",
      thankYouMessage: "Мы получили вашу заявку и свяжемся с вами в ближайшее время.",
      responseTime: "Среднее время ответа: 2-3 часа"
    },
    en: {
      title: "Contact Us",
      subtitle: "Ready to discuss your project? Fill out the form below, and we'll get back to you as soon as possible.",
      formTitle: "Tell us about your project",
      nameLabel: "Name *",
      namePlaceholder: "Your name",
      nameError: "Please enter your name",
      emailLabel: "Email *",
      emailPlaceholder: "Your email",
      emailError: "Please enter your email",
      emailInvalidError: "Invalid email",
      phoneLabel: "Phone (optional)",
      phonePlaceholder: "Your phone number",
      budgetLabel: "Budget (optional)",
      budgetPlaceholder: "Select budget",
      timelineLabel: "Timeline (optional)",
      messageLabel: "Message *",
      messagePlaceholder: "Tell us more about your project...",
      messageError: "Please enter your message",
      submitButton: "Send Message",
      sending: "Sending...",
      privacy: "By clicking 'Send', you agree to our",
      privacyLink: "privacy policy",
      thankYouTitle: "Thank you for your message!",
      thankYouMessage: "We've received your inquiry and will contact you shortly.",
      responseTime: "Average response time: 2-3 hours"
    }
  };
  
  // Get translated text
  const getText = (key) => translations[currentLanguage]?.[key] || translations.ru[key];
  
  // Budget options based on language
  const budgetOptions = currentLanguage === 'en' 
    ? [
        'Up to $1,000',
        '$1,000 - $3,000',
        '$3,000 - $6,000',
        'More than $6,000',
        'Not determined'
      ]
    : [
        'До 100 000 ₽',
        '100 000 - 300 000 ₽',
        '300 000 - 600 000 ₽',
        'Более 600 000 ₽',
        'Не определен'
      ];
  
  // Timeline options based on language
  const timelineOptions = currentLanguage === 'en'
    ? [
        'As soon as possible',
        'Within a month',
        'Within 2-3 months',
        'More than 3 months'
      ]
    : [
        'Как можно скорее',
        'В течение месяца',
        'В течение 2-3 месяцев',
        'Более 3 месяцев'
      ];
  
  // Handle visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.2 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formState.name.trim()) {
      errors.name = getText('nameError');
    }
    
    if (!formState.email.trim()) {
      errors.email = getText('emailError');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      errors.email = getText('emailInvalidError');
    }
    
    if (!formState.message.trim()) {
      errors.message = getText('messageError');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Prepare form data for Web3Forms
    const formData = {
      name: formState.name,
      email: formState.email,
      phone: formState.phone,
      message: formState.message,
      budget: formState.budget,
      timeline: formState.timeline,
      access_key: "65843dcd-2b63-43b1-8a2f-f2941ae9782d"
    };
    
    // Convert to JSON
    const jsonData = JSON.stringify(formData);
    
    // Send data to Web3Forms API
    fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: jsonData
    })
    .then(async (response) => {
      if (response.ok) {
        setIsSubmitting(false);
        setIsSubmitted(true);
        
        // Reset form after some time
        setTimeout(() => {
          setIsSubmitted(false);
          setFormState({
            name: '',
            email: '',
            phone: '',
            message: '',
            budget: '',
            timeline: ''
          });
        }, 6000);
      } else {
        const errorResponse = await response.json();
        console.error('Error submitting form:', errorResponse);
        setIsSubmitting(false);
        // Here you might want to show an error message to the user
      }
    })
    .catch((error) => {
      console.error('Error during form submission:', error);
      setIsSubmitting(false);
      // Here you might want to show an error message to the user
    });
  };
  
  return (
    <section
      ref={sectionRef}
      id="contact"
      className={`relative py-20 overflow-hidden ${
        currentTheme === 'light' 
          ? 'bg-white' 
          : 'bg-[#080808]'
      }`}
    >
      {/* Декоративные элементы */}
      <div className="absolute inset-0 pointer-events-none">
        {currentTheme === 'light' ? (
          <>
            <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-gradient-to-bl from-blue-50 to-transparent"></div>
            <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-tr from-indigo-50 to-transparent"></div>
          </>
        ) : (
          <>
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-dark-secondary/10 rounded-full blur-3xl opacity-20"></div>
            <div className="absolute bottom-20 -left-20 w-80 h-80 bg-dark-secondary/10 rounded-full blur-3xl opacity-20"></div>
          </>
        )}
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="mb-16 text-center">
          <h2 className={`text-4xl md:text-5xl font-bold mb-6 ${
            currentTheme === 'light' 
              ? 'text-gray-900' 
              : 'text-white'
          }`}>
            {getText('title')}
          </h2>
          
          <p className={`max-w-2xl mx-auto ${
            currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'
          }`}>
            {getText('subtitle')}
          </p>
        </div>
        
        <div className="grid grid-cols-1">
          {/* Форма */}
          <div>
            {isSubmitted ? (
              <div className={`h-full rounded-2xl p-10 flex flex-col items-center justify-center ${
                currentTheme === 'light'
                  ? 'bg-white shadow-xl shadow-gray-200/70'
                  : 'bg-gradient-to-br from-[#111] to-[#0A0A0A] border border-gray-800'
              }`}>
                <div className={`w-20 h-20 rounded-full mb-6 flex items-center justify-center ${
                  currentTheme === 'light'
                    ? 'bg-green-100 text-green-600'
                    : 'bg-green-900/20 text-green-400'
                }`}>
                  <FaCheckCircle className="text-4xl" />
                </div>
                <h3 className={`text-2xl font-bold mb-4 ${
                  currentTheme === 'light' ? 'text-gray-900' : 'text-white'
                }`}>{getText('thankYouTitle')}</h3>
                <p className={`text-center max-w-md mb-8 ${
                  currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {getText('thankYouMessage')}
                </p>
                <div className={`flex items-center justify-center space-x-2 text-sm ${
                  currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  <FaRegClock />
                  <span>{getText('responseTime')}</span>
                </div>
              </div>
            ) : (
              <div className={`rounded-2xl ${
                currentTheme === 'light'
                  ? 'bg-white shadow-xl shadow-gray-200/70'
                  : 'bg-gradient-to-br from-[#111] to-[#0A0A0A] border border-gray-800'
              }`}>
                <form onSubmit={handleSubmit} className="p-8">
                  <input type="hidden" name="access_key" value="65843dcd-2b63-43b1-8a2f-f2941ae9782d" />
                  <h3 className={`text-xl font-bold mb-6 ${
                    currentTheme === 'light' ? 'text-gray-900' : 'text-white'
                  }`}>{getText('formTitle')}</h3>
                  
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Имя */}
                      <div>
                        <label 
                          className={`text-sm font-medium block mb-2 ${
                            currentTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
                          }`}
                        >
                          {getText('nameLabel')} *
                        </label>
                        <div className="relative">
                          <div className={`absolute left-4 inset-y-0 flex items-center pointer-events-none ${
                            currentTheme === 'light' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            <FaUserAlt />
                          </div>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formState.name}
                            onChange={handleInputChange}
                            onFocus={() => setFocusedField('name')}
                            onBlur={() => setFocusedField(null)}
                            placeholder={getText('namePlaceholder')}
                            className={`w-full pl-10 pr-4 py-3 rounded-lg outline-none ${
                              currentTheme === 'light'
                                ? 'bg-gray-50 border border-gray-200 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50'
                                : `bg-[#0D0D0D] border text-white ${
                                  focusedField === 'name' 
                                    ? 'border-[#00F0FF]' 
                                    : formErrors.name 
                                      ? 'border-red-500' 
                                      : 'border-[#333]'
                                }`
                            } transition-all duration-200`}
                          />
                        </div>
                        {formErrors.name && (
                          <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                        )}
                      </div>
                      
                      {/* Email */}
                      <div>
                        <label 
                          className={`text-sm font-medium block mb-2 ${
                            currentTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
                          }`}
                        >
                          {getText('emailLabel')} *
                        </label>
                        <div className="relative">
                          <div className={`absolute left-4 inset-y-0 flex items-center pointer-events-none ${
                            currentTheme === 'light' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            <FaEnvelope />
                          </div>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formState.email}
                            onChange={handleInputChange}
                            onFocus={() => setFocusedField('email')}
                            onBlur={() => setFocusedField(null)}
                            placeholder={getText('emailPlaceholder')}
                            className={`w-full pl-10 pr-4 py-3 rounded-lg outline-none ${
                              currentTheme === 'light'
                                ? 'bg-gray-50 border border-gray-200 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50'
                                : `bg-[#0D0D0D] border text-white ${
                                  focusedField === 'email' 
                                    ? 'border-[#00F0FF]' 
                                    : formErrors.email 
                                      ? 'border-red-500' 
                                      : 'border-[#333]'
                                }`
                            } transition-all duration-200`}
                          />
                        </div>
                        {formErrors.email && (
                          <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Телефон */}
                      <div>
                        <label 
                          className={`text-sm font-medium block mb-2 ${
                            currentTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
                          }`}
                        >
                          {getText('phoneLabel')}
                        </label>
                        <div className="relative">
                          <div className={`absolute left-4 inset-y-0 flex items-center pointer-events-none ${
                            currentTheme === 'light' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            <FaPhoneAlt />
                          </div>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formState.phone}
                            onChange={handleInputChange}
                            onFocus={() => setFocusedField('phone')}
                            onBlur={() => setFocusedField(null)}
                            placeholder={getText('phonePlaceholder')}
                            className={`w-full pl-10 pr-4 py-3 rounded-lg outline-none ${
                              currentTheme === 'light'
                                ? `bg-gray-50 border text-gray-900 ${
                                  focusedField === 'phone' 
                                    ? 'border-light-primary ring-1 ring-light-primary/50' 
                                    : 'border-gray-200'
                                  }`
                                : `bg-[#0D0D0D] border text-white ${
                                  focusedField === 'phone' 
                                    ? 'border-dark-primary' 
                                    : 'border-[#333]'
                                }`
                            } transition-all duration-200`}
                          />
                        </div>
                      </div>
                      
                      {/* Бюджет */}
                      <div>
                        <label 
                          className={`text-sm font-medium block mb-2 ${
                            currentTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
                          }`}
                        >
                          {getText('budgetLabel')}
                        </label>
                        <div className="relative">
                          <select
                            id="budget"
                            name="budget"
                            value={formState.budget}
                            onChange={handleInputChange}
                            className={`w-full pl-4 pr-10 py-3 rounded-lg appearance-none outline-none ${
                              currentTheme === 'light'
                                ? 'bg-gray-50 border border-gray-200 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50'
                                : `bg-[#0D0D0D] border text-white ${
                                  focusedField === 'budget' 
                                    ? 'border-[#00F0FF]' 
                                    : 'border-[#333]'
                                }`
                            } transition-all duration-200`}
                          >
                            <option value="">{getText('budgetPlaceholder')}</option>
                            {budgetOptions.map((option, index) => (
                              <option key={index} value={option}>{option}</option>
                            ))}
                          </select>
                          <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                            <FaChevronDown className={currentTheme === 'light' ? 'text-gray-400' : 'text-gray-500'} />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Сроки */}
                    <div>
                      <label 
                        className={`text-sm font-medium block mb-2 ${
                          currentTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
                        }`}
                      >
                        {getText('timelineLabel')}
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {timelineOptions.map((option, index) => (
                          <div 
                            key={index} 
                            className={`
                              cursor-pointer rounded-lg p-3 text-center text-sm transition-all duration-200
                              ${formState.timeline === option 
                                ? currentTheme === 'light'
                                  ? 'bg-light-primary/10 border border-light-primary text-light-primary'
                                  : 'bg-dark-primary/10 border border-dark-primary text-dark-primary'
                                : currentTheme === 'light'
                                  ? 'bg-gray-50 border border-gray-200 text-gray-700 hover:border-light-primary/50'
                                  : 'bg-[#0D0D0D] border border-[#333] text-gray-300 hover:border-dark-primary/50'
                              }
                            `}
                            onClick={() => handleInputChange({ target: { name: 'timeline', value: option } })}
                          >
                            {option}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Сообщение */}
                    <div>
                      <label 
                        className={`text-sm font-medium block mb-2 ${
                          currentTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
                        }`}
                      >
                        {getText('messageLabel')} *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows="4"
                        value={formState.message}
                        onChange={handleInputChange}
                        onFocus={() => setFocusedField('message')}
                        onBlur={() => setFocusedField(null)}
                        placeholder={getText('messagePlaceholder')}
                        className={`w-full px-4 py-3 rounded-lg outline-none ${
                          currentTheme === 'light'
                            ? 'bg-gray-50 border border-gray-200 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50'
                            : `bg-[#0D0D0D] border text-white ${
                              focusedField === 'message' 
                                ? 'border-[#00F0FF]' 
                                : formErrors.message 
                                  ? 'border-red-500' 
                                  : 'border-[#333]'
                            }`
                        } transition-all duration-200`}
                      ></textarea>
                      {formErrors.message && (
                        <p className="text-red-500 text-xs mt-1">{formErrors.message}</p>
                      )}
                    </div>
                    
                    {/* Submit button */}
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`
                          w-full py-3 px-6 rounded-lg font-medium flex items-center justify-center transition-all duration-300
                          ${isSubmitting
                            ? 'cursor-not-allowed opacity-70'
                            : currentTheme === 'light'
                              ? 'bg-gradient-to-r from-light-primary to-light-secondary text-white hover:shadow-lg hover:shadow-light-primary/20'
                              : 'bg-gradient-to-r from-dark-primary to-dark-secondary text-white hover:shadow-lg hover:shadow-dark-primary/20'
                          }
                        `}
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {getText('sending')}
                          </>
                        ) : (
                          <>
                            {getText('submitButton')}
                            <FaArrowRight className="ml-2" />
                          </>
                        )}
                      </button>
                      
                      <p className={`text-center mt-4 text-xs ${
                        currentTheme === 'light' ? 'text-gray-500' : 'text-gray-500'
                      }`}>
                        {getText('privacy')}
                        <a 
                          href="/privacy" 
                          className={currentTheme === 'light' ? 'text-light-primary underline ml-1' : 'text-dark-primary underline ml-1'}
                        >
                          {getText('privacyLink')}
                        </a>
                      </p>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm; 